import { useState } from 'react';
import { QueryClient, QueryClientProvider, useQuery } from '@tanstack/react-query';
import { cx } from '@parrot/utils';
import './styles/tokens.css';
import type { ProductClickHandler } from './types';
import { createPlatformApi } from './api';
import { AdminView } from './components/AdminView/AdminView';
import { CustomerView } from './components/CustomerView/CustomerView';
import { Hero } from './components/Hero/Hero';
import styles from './ProductSuite.module.css';

const TITLE = 'The Media Operating System';
const SUBTITLE_MESSAGES = [
  'Intelligence for the entire content value chain.',
  'Powering smarter decisions for studios and streamers.',
  'De-risking investments from greenlight to global screen.',
  'Audience intelligence for talent, sports, and brands.',
  'Trusted by PE firms, governments, and distributors worldwide.',
  'From concept to consumption — data at every stage.',
];

const api = createPlatformApi();

interface ProductSuiteProps {
  /** Merged onto the component root, for host-app layout (margin, width, etc.). */
  className?: string;
  /**
   * Colour theme. Omit and the widget uses its dark palette; pass `'light'` to
   * switch. An embedding app should pass its own theme state so the widget stays
   * in lockstep with the surrounding UI — it cannot yet inherit the document's
   * `data-theme`, because these tokens are scoped to the component's own root.
   */
  theme?: 'dark' | 'light';
  /**
   * Called when a product card is clicked. Provide it to own navigation — SSO
   * launch, in-app routing, blocking unlicensed — by calling `event.preventDefault()`
   * and acting on `product`. Omit it and cards fall back to a plain external link.
   */
  onProductClick?: ProductClickHandler;
}

/**
 * Self-contained showcase of Parrot's product suite. Fetches its own data from
 * the platform API and renders it — nothing to configure, no data to pass in.
 */
export function ProductSuite({ className, theme, onProductClick }: ProductSuiteProps = {}) {
  // A private QueryClient — never touches the host app's, so no provider setup is
  // required of consumers. `useState` keeps one stable instance per mount.
  const [queryClient] = useState(
    () =>
      new QueryClient({
        defaultOptions: { queries: { retry: 1, refetchOnWindowFocus: false, staleTime: 60_000 } },
      }),
  );
  return (
    <QueryClientProvider client={queryClient}>
      <ProductSuiteInner className={className} theme={theme} onProductClick={onProductClick} />
    </QueryClientProvider>
  );
}

function ProductSuiteInner({ className, theme, onProductClick }: ProductSuiteProps) {
  const productsQuery = useQuery({
    queryKey: ['ps', 'products'],
    queryFn: () => api.listProducts(),
  });
  const meQuery = useQuery({ queryKey: ['ps', 'me'], queryFn: () => api.me() });
  const isAdmin = meQuery.data?.role === 'admin';

  // Industries only power the admin sidebar — customers get server-recommended lanes.
  const industriesQuery = useQuery({
    queryKey: ['ps', 'industries'],
    queryFn: () => api.listIndustries(),
    enabled: isAdmin,
  });

  const products = productsQuery.data?.products ?? [];
  const industries = industriesQuery.data?.industries ?? [];

  const loading =
    productsQuery.isLoading || meQuery.isLoading || (isAdmin && industriesQuery.isLoading);
  const failed = productsQuery.isError || meQuery.isError;

  const baseClassName = cx('parrot-product-suite', styles.base, className);

  if (loading) {
    return (
      <div className={baseClassName} data-theme={theme}>
        <div className={styles.status}>Loading products…</div>
      </div>
    );
  }
  if (failed) {
    return (
      <div className={baseClassName} data-theme={theme}>
        <div className={styles.status}>Couldn’t load products. Please try again.</div>
      </div>
    );
  }

  return (
    <div className={baseClassName} data-theme={theme}>
      <Hero title={TITLE} messages={SUBTITLE_MESSAGES} />
      {isAdmin ? (
        <AdminView products={products} industries={industries} onProductClick={onProductClick} />
      ) : (
        <CustomerView products={products} onProductClick={onProductClick} />
      )}
    </div>
  );
}
